import { useWallet } from "@manahippo/aptos-wallet-adapter";
import { Slider } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import { useState, MouseEvent, ChangeEvent, useEffect } from "react";
import { useDispatch } from "react-redux";

import Button from "../../components/Common/Button";
import TransactionDialog from "../../components/Common/TransactionDialog";
import SearchBox from "../../components/Vote/SearchBox";
import { getTransactionByHash } from "../../libs/aptos_sdk";
import { PoolVote } from "../../libs/pool";
import {
  get_all_pairs_vote,
  get_coin_icon,
  getDecimalsAmount,
  vote,
} from "../../libs/swap_sdk";
import { end_loading, start_loading } from "../../store/loading";
import { sleep } from "../../utils/common";

interface VotingPowerList {
  [key: string]: number;
}

export default function Vote() {
  const { signAndSubmitTransaction, account } = useWallet();
  const [poolList, setPoolList] = useState<PoolVote[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [votingPowerList, setVotingPoolList] = useState<VotingPowerList>({});
  const [totalVotingPoolPercent, setTotalVotingPoolPercent] =
    useState<number>(0);
  const [tokenId, setTokenId] = useState<number>(0);
  const dispatch = useDispatch();

  const [txn, setTxn] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);

  useEffect(() => {
    const fetch = async () => {
      if (!account || !account.address) return;
      dispatch(start_loading());
      const poolsGauge = await get_all_pairs_vote(
        account.address as string,
        tokenId
      );
      setPoolList(poolsGauge);
      dispatch(end_loading());
    };

    if (account) {
      fetch();
    } else {
      setPoolList([]);
    }
  }, [account, tokenId]);

  const handleChangePage = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePercent = (event: any, pool_addr: string) => {
    const votingPoolPercent = event.target.value;

    setVotingPoolList((votingPoolList) => {
      votingPoolList[pool_addr] = votingPoolPercent;
      return votingPoolList;
    });

    let sum = 0;
    for (const pool_addr1 in votingPowerList) {
      if (pool_addr1 !== pool_addr) {
        sum = Math.abs(votingPowerList[pool_addr1]);
      }
    }
    setTotalVotingPoolPercent(sum + Math.abs(votingPoolPercent));
  };

  const handle_vote = async () => {
    if (!account || !account.address || tokenId === 0) return;
    setTxn("");
    setOpenAlert(true);
    setStep(0);

    const pool_votes: string[] = [];
    const weights: number[] = [];

    for (const pool_addr in votingPowerList) {
      pool_votes.push(pool_addr);
      weights.push(votingPowerList[pool_addr]);
    }

    const data = await vote(
      signAndSubmitTransaction,
      tokenId,
      pool_votes,
      weights
    );

    setTxn(data.hash);
    setStep(1);
    let transaction;
    while (!transaction) {
      try {
        transaction = await getTransactionByHash(data.hash);
      } catch {
        await sleep(500);
      }
    }
    setStep(2);
  };
  const handleCloseAlert = () => setOpenAlert(false);
  return (
    <div className="h-screen overflow-y-auto">
      <div className="min-h-screen flex pt-16 pb-4">
        <div className="flex flex-col w-full gap-8 p-10 max-md:p-5">
          <SearchBox setTokenId={setTokenId} />

          <div className="bg-white rounded-lg bg-opacity-10">
            <div className="w-full max-w-full overflow-x-scroll">
              <div className="w-full max-xl:w-[1000px]">
                <table className="w-full">
                  <thead className="bg-white bg-opacity-10 text-white w-full mb-3">
                    <tr>
                      <th className="py-3 pl-3 w-[25%] text-left">Asset</th>
                      <th className="text-left">My Stake</th>
                      <th className="text-left">Total Liquidity</th>
                      <th className="text-left">Total Votes</th>
                      <th className="text-left">Bribes</th>
                      <th className="text-left w-[10%]">My Votes</th>
                      <th className="text-left w-[10%]">My Votes %</th>
                    </tr>
                  </thead>
                  <tbody className="text-white">
                    {(rowsPerPage > 0
                      ? poolList.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                      : poolList
                    ).map((item: PoolVote, index: number) => {
                      return (
                        <tr
                          className="overflow-X-scroll"
                          key={`vote_key_${index}`}
                        >
                          <td className="flex pl-3 py-2 gap-5">
                            <div className="flex justify-center items-center ">
                              <img
                                alt="Coin"
                                className="w-10 h-10"
                                src={
                                  get_coin_icon(item.pool.x_coin_info.address)
                                    .icon
                                }
                              />
                              <img
                                alt="Coin"
                                className="w-10 h-10 -ml-4"
                                src={
                                  get_coin_icon(item.pool.y_coin_info.address)
                                    .icon
                                }
                              />
                            </div>
                            <div className="flex justify-center items-center">
                              <p className="font-semibold">{`${item.pool.x_coin_info.symbol}-${item.pool.y_coin_info.symbol}`}</p>
                            </div>
                          </td>
                          <td className="flex-col justify-center">
                            <div className="flex">
                              <p className="font-semibold">
                                {item.my_x_balance}
                              </p>
                              <p className="text-white text-opacity-50">
                                {item.pool.x_coin_info.symbol}
                              </p>
                            </div>
                            <div className="flex">
                              <p className="font-semibold">
                                {item.my_y_balance}
                              </p>
                              <p className="text-white text-opacity-50">
                                {item.pool.y_coin_info.symbol}
                              </p>
                            </div>
                          </td>
                          <td className="flex-col justify-center">
                            <div className="flex">
                              <p className="font-semibold">
                                {getDecimalsAmount(
                                  Number(item.pool.reserve_x),
                                  item.pool.x_coin_info.decimals
                                )}
                              </p>
                              <p className="text-white text-opacity-50">
                                {item.pool.x_coin_info.symbol}
                              </p>
                            </div>
                            <div className="flex">
                              <p className="font-semibold">
                                {getDecimalsAmount(
                                  Number(item.pool.reserve_x),
                                  item.pool.x_coin_info.decimals
                                )}
                              </p>
                              <p className="text-white text-opacity-50">
                                {item.pool.y_coin_info.symbol}
                              </p>
                            </div>
                          </td>
                          <td className="flex-col justify-center">
                            <p className="font-semibold">{item.weight}</p>
                            <p className="text-white text-opacity-50">
                              {item.weight_percent} %
                            </p>
                          </td>
                          <td className="flex-col justify-center">
                            {item.reward_amounts.map((reward, index) => {
                              return (
                                <div
                                  className="flex"
                                  key={`reward_key_${index}`}
                                >
                                  <p className="font-semibold">
                                    {reward.reward_amount}
                                  </p>
                                  <p className="text-white text-opacity-50">
                                    {reward.symbol}
                                  </p>
                                </div>
                              );
                            })}
                          </td>
                          <td className="flex-col justify-center">
                            <p className="font-semibold">{item.my_votes}</p>
                            <p className="text-white text-opacity-50">
                              {item.my_votes_percent} %
                            </p>
                          </td>
                          <td className="flex-col justify-center pr-3">
                            <Slider
                              className="w-full"
                              valueLabelDisplay="auto"
                              step={1}
                              min={-100}
                              max={100}
                              defaultValue={0}
                              value={votingPowerList[item.pool_addr] ?? 0}
                              onChange={(event) =>
                                handleChangePercent(event, item.pool_addr)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="w-full pr-3 sm:pr-0 flex justify-end max-xl:justify-start items-center">
              <TablePagination
                className="pagination"
                component="div"
                count={poolList.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </div>

          <div className="fixed bottom-10 left-0 w-full">
            <div className="w-[400px] mx-auto flex items-center justify-between bg-primary-200 py-3 px-6 rounded-xl">
              <div className="flex items-center gap-2">
                <p className="text-white">Voting Power Used:</p>
                <p className="text-[#39ff88] text-lg font-bold">
                  {totalVotingPoolPercent} %
                </p>
              </div>
              <Button
                disabled={
                  totalVotingPoolPercent === 0 || totalVotingPoolPercent > 100
                }
                onClick={handle_vote}
              >
                Cast Votes
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div>
        <TransactionDialog
          openAlert={openAlert}
          txn={txn}
          title="Vote"
          step={step}
          handleCloseAlert={handleCloseAlert}
        />
      </div>
    </div>
  );
}
